import type { GridFilterItem } from '@mui/x-data-grid-premium';
import {
  gridAggregationModelSelector,
  gridColumnFieldsSelector,
  gridColumnLookupSelector,
  gridColumnVisibilityModelSelector,
  gridDensitySelector,
  gridFilterModelSelector,
  GridLogicOperator,
  gridPageSizeSelector,
  gridPinnedColumnsSelector,
  gridRowGroupingModelSelector,
  gridSortModelSelector,
} from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import type { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { isEqual } from 'lodash-es';
import type { MutableRefObject } from 'react';

import { activeFilterItems } from '~/components/table/helpers';

/** How the table is arranged. Where you are in it — scroll, open panels — is not part of a view. */
export type GridViewState = Pick<
  GridInitialStatePremium,
  | 'columns'
  | 'filter'
  | 'sorting'
  | 'density'
  | 'rowGrouping'
  | 'aggregation'
  | 'pinnedColumns'
  | 'pagination'
>;

export type GridView = {
  id: string;
  name: string;
  state: GridViewState;
  /** Optional: views saved before this was recorded sort to the front. */
  createdAt?: number;
};

export type StoredGridViews = {
  views: GridView[];
  /** `null` means the built-in view is the one that opens. */
  defaultViewId: string | null;
};

export const EMPTY_STORED_VIEWS: StoredGridViews = { views: [], defaultViewId: null };

type GridApiRef = MutableRefObject<GridApiPremium>;

/**
 * Filter items the page owns — the global filter bar's portfolio, region and service
 * provider. They say where the reader is, not how they arranged the table, so a view never
 * stores them and the live ones ride across every switch.
 */
export type ExternalFilterItemIds = readonly string[];

const isExternal = (item: GridFilterItem, externalIds: ExternalFilterItemIds) =>
  externalIds.includes(String(item.id));

// Through JSON so a live model compares equal to the same model read back from storage.
const normalize = (state: GridViewState): GridViewState =>
  JSON.parse(JSON.stringify(state)) as GridViewState;

/**
 * Every dimension cut down to the columns the table actually has, so both sides of a
 * comparison are cut the same way. A field can go missing between the two for ordinary
 * reasons — a column removed in a release, or one a role does not get — and MUI keeps the
 * stale entry rather than dropping it, so sanitising only the stored side left the marker
 * stuck on with nothing the reader could do about it.
 */
function restrictToLiveFields(apiRef: GridApiRef, state: GridViewState): GridViewState {
  const fields = new Set(apiRef.current.getAllColumns().map((column) => column.field));
  const live = (candidates: string[] = []) => candidates.filter((field) => fields.has(field));
  const byField = <T>(model: Record<string, T> = {}) =>
    Object.fromEntries(Object.entries(model).filter(([field]) => fields.has(field)));

  // Grouped columns are hidden by `applyViewState`, so the comparison has to expect that.
  const columnVisibilityModel = byField(state.columns?.columnVisibilityModel);
  for (const field of live(state.rowGrouping?.model)) {
    columnVisibilityModel[field] = false;
  }

  return {
    ...state,
    columns: { ...state.columns, columnVisibilityModel },
    sorting: {
      sortModel: (state.sorting?.sortModel ?? []).filter((item) => fields.has(item.field)),
    },
    rowGrouping: { model: live(state.rowGrouping?.model) },
    pinnedColumns: {
      left: live(state.pinnedColumns?.left),
      right: live(state.pinnedColumns?.right),
    },
    aggregation: { model: byField(state.aggregation?.model) },
  };
}

/**
 * The subset of `items` that actually narrows the table; see `activeFilterItems`. Every
 * stored view passes through here before it reaches the grid, and so does the state saved
 * earlier in this session — which went through JSON too — so this, rather than the storage
 * read, is where a date filter's value becomes a `Date` again.
 */
function effectiveFilterItems(apiRef: GridApiRef, items: GridFilterItem[]): GridFilterItem[] {
  return activeFilterItems(gridColumnLookupSelector(apiRef), items);
}

/** Selectors rather than `exportState()`, so this can also be read from inside a grid event. */
export function readViewState(
  apiRef: GridApiRef,
  externalFilterItemIds: ExternalFilterItemIds = [],
): GridViewState {
  const filterModel = gridFilterModelSelector(apiRef);

  return normalize(
    restrictToLiveFields(apiRef, {
      // Rows per page only, so turning a page is not a change. `restoreState` refills `page` as 0.
      pagination: { paginationModel: { pageSize: gridPageSizeSelector(apiRef) } },
      columns: {
        columnVisibilityModel: gridColumnVisibilityModelSelector(apiRef),
        orderedFields: gridColumnFieldsSelector(apiRef),
      },
      filter: {
        filterModel: {
          items: effectiveFilterItems(
            apiRef,
            filterModel.items.filter((item) => !isExternal(item, externalFilterItemIds)),
          ),
          // Present on some paths and absent on others; spelled out so that is not a difference.
          logicOperator: filterModel.logicOperator ?? GridLogicOperator.And,
        },
      },
      sorting: { sortModel: gridSortModelSelector(apiRef) },
      density: gridDensitySelector(apiRef.current.state),
      rowGrouping: { model: gridRowGroupingModelSelector(apiRef) },
      aggregation: { model: gridAggregationModelSelector(apiRef) },
      pinnedColumns: gridPinnedColumnsSelector(apiRef.current.state),
    }),
  );
}

/**
 * A stored view reduced to what the grid can actually hold, so "has this changed?" compares
 * like with like. The grid silently declines a filter on a removed column, an operator it no
 * longer offers, or a row left with no value — comparing against the raw stored copy reported
 * a change the reader could neither see nor undo.
 */
export function comparableViewState(
  apiRef: GridApiRef,
  baseline: GridViewState,
  view: GridViewState,
  externalFilterItemIds: ExternalFilterItemIds = [],
): GridViewState {
  // Merged as `applyViewState` merges it: a view silent about a dimension leaves the
  // table's own value in place, and that absence is not a difference.
  const state: GridViewState = { ...baseline, ...view };

  return restrictToLiveFields(apiRef, {
    ...state,
    filter: {
      filterModel: {
        ...state.filter?.filterModel,
        items: effectiveFilterItems(
          apiRef,
          (state.filter?.filterModel?.items ?? []).filter(
            (item) => !isExternal(item, externalFilterItemIds),
          ),
        ),
      },
    },
  });
}

/** Column order, judged only over the columns both sides actually have. */
const sameFieldOrder = (a: string[] = [], b: string[] = []): boolean => {
  const inA = new Set(a);
  const inB = new Set(b);
  return isEqual(
    a.filter((field) => inB.has(field)),
    b.filter((field) => inA.has(field)),
  );
};

/** One spelling per arrangement, so only a real difference reads as one. */
const canonical = (state: GridViewState) => {
  const { columns, filter, sorting, rowGrouping, aggregation, pinnedColumns, ...rest } =
    normalize(state);

  return {
    ...rest,
    columns: {
      columnVisibilityModel: Object.fromEntries(
        Object.entries(columns?.columnVisibilityModel ?? {}).filter(([, shown]) => shown === false),
      ),
    },
    filter: {
      filterModel: {
        // Ids dropped and order settled: MUI mints a random id per item, so retyping the
        // same filter compared as a change. Order is safe to ignore — the panel is AND-only.
        items: (filter?.filterModel?.items ?? [])
          .map(({ field, operator, value }) => ({ field, operator, value }))
          .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
        logicOperator: filter?.filterModel?.logicOperator ?? GridLogicOperator.And,
      },
    },
    sorting: { sortModel: sorting?.sortModel ?? [] },
    rowGrouping: { model: rowGrouping?.model ?? [] },
    aggregation: { model: aggregation?.model ?? {} },
    pinnedColumns: { left: pinnedColumns?.left ?? [], right: pinnedColumns?.right ?? [] },
  };
};

export const isSameViewState = (a: GridViewState, b: GridViewState): boolean =>
  isEqual(canonical(a), canonical(b)) &&
  sameFieldOrder(a.columns?.orderedFields, b.columns?.orderedFields);

/**
 * `restoreState` merges, so the baseline carries an empty model for every dimension and
 * switching views resets rather than accumulates. The page's filter items and the search
 * term survive the switch, being no part of the arrangement. Grouped columns are forced
 * hidden because `useKeepGroupedColumnsHidden` owns that rule.
 */
export function applyViewState(
  apiRef: GridApiRef,
  baseline: GridViewState,
  view: GridViewState | null,
  externalFilterItemIds: ExternalFilterItemIds = [],
  /**
   * Keep the filter items already on the grid. True only on arrival, where a link that came
   * in pre-filtered has seeded them: those still apply, on top of the view and ahead of it,
   * and show as an edit. Switching views later replaces them.
   */
  keepSeededFilterItems = false,
): void {
  const merged: GridViewState = { ...baseline, ...(view ?? {}) };
  const columnVisibilityModel = { ...merged.columns?.columnVisibilityModel };
  for (const field of merged.rowGrouping?.model ?? []) {
    columnVisibilityModel[field] = false;
  }

  const live = gridFilterModelSelector(apiRef);
  const external = live.items.filter((item) => isExternal(item, externalFilterItemIds));
  const seeded = keepSeededFilterItems
    ? effectiveFilterItems(
        apiRef,
        live.items.filter((item) => !isExternal(item, externalFilterItemIds)),
      )
    : [];
  // On an id collision the link wins. Pages build their items with fixed ids (`status`,
  // `entryType`, `number`), so a view saved while the page was deep-linked holds the same
  // id — and putting the stored value back would show the reader rows they did not ask for,
  // with nothing on screen to say so.
  const seededIds = new Set(seeded.map((item) => String(item.id)));
  const own = effectiveFilterItems(
    apiRef,
    (merged.filter?.filterModel?.items ?? []).filter(
      (item) => !isExternal(item, externalFilterItemIds) && !seededIds.has(String(item.id)),
    ),
  );

  apiRef.current.restoreState({
    ...merged,
    columns: { ...merged.columns, columnVisibilityModel },
    filter: {
      filterModel: {
        ...merged.filter?.filterModel,
        items: [...external, ...seeded, ...own],
        quickFilterValues: live.quickFilterValues,
        quickFilterLogicOperator: live.quickFilterLogicOperator,
      },
    },
  });
}

/**
 * Clear Filters: back to the filters the active view holds, not to none. Only the filter
 * slice moves — columns, sorting and density belong to the reset entry. The global filter
 * bar keeps its own items; the search box is emptied because the button speaks for it too.
 */
export function applyViewFilters(
  apiRef: GridApiRef,
  view: GridViewState | null,
  externalFilterItemIds: ExternalFilterItemIds = [],
): void {
  const live = gridFilterModelSelector(apiRef);
  const external = live.items.filter((item) => isExternal(item, externalFilterItemIds));
  const own = effectiveFilterItems(
    apiRef,
    (view?.filter?.filterModel?.items ?? []).filter(
      (item) => !isExternal(item, externalFilterItemIds),
    ),
  );

  apiRef.current.restoreState({
    filter: {
      filterModel: {
        items: [...external, ...own],
        logicOperator: view?.filter?.filterModel?.logicOperator ?? GridLogicOperator.And,
        quickFilterValues: [],
        quickFilterLogicOperator: GridLogicOperator.And,
      },
    },
  });
}
